// src/pages/HomePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Star, Shield, Truck, Gift,
  ChevronLeft, ChevronRight, RefreshCw,
} from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { SkeletonCard } from '../components/Loader';
import { useProducts } from '../hooks/useProducts';
import { TESTIMONIALS, HERO_SLIDES, RECIPIENT_CATEGORIES } from '../data/products';

// ─────────────────────────────────────────────────────────────────
// HERO SLIDER
// Real romantic photos + deep rose/pink overlay → premium feel
// ─────────────────────────────────────────────────────────────────
function HeroSlider() {
  const [current,  setCurrent]  = useState(0);
  const [animating, setAnimating] = useState(false);

  const go = useCallback((idx) => {
    if (animating || idx === current) return;
    setAnimating(true);
    setCurrent(idx);
    setTimeout(() => setAnimating(false), 700);
  }, [animating, current]);

  useEffect(() => {
    const t = setInterval(() => go((current + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, [current, go]);

  const slide = HERO_SLIDES[current];

  return (
    <div className="relative overflow-hidden" style={{ minHeight: '100vh' }}>

      {/* ── Background Photo ── */}
      <div className="absolute inset-0">
        <img
          key={current}
          src={slide.image}
          alt=""
          className="w-full h-full object-cover"
          style={{ animation: 'heroZoom 7s ease-out forwards' }}
        />

        {/* Deep rose-pink overlay — vivid, premium, brand-coloured */}
        <div className="absolute inset-0"
          style={{
            background: 'linear-gradient(110deg, rgba(136,19,55,0.82) 0%, rgba(190,24,93,0.70) 40%, rgba(244,63,94,0.45) 70%, rgba(251,113,133,0.20) 100%)',
          }}
        />

        {/* Subtle vignette at bottom so it blends into white */}
        <div className="absolute bottom-0 left-0 right-0 h-40"
          style={{ background: 'linear-gradient(to top, white 0%, transparent 100%)' }} />
      </div>

      {/* ── Decorative circles ── */}
      <div className="absolute top-24 right-12 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #fda4af, transparent)' }} />
      <div className="absolute bottom-32 left-8 w-40 h-40 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #fb7185, transparent)' }} />

      {/* ── Content ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
        style={{ minHeight: '100vh', paddingTop: '7rem', paddingBottom: '5rem' }}>
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">

          {/* Left — text */}
          <div>
            {/* Pill tag */}
            <span className="inline-flex items-center gap-2 border border-rose-300/50 bg-white/15 backdrop-blur-sm text-white text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-rose-300 rounded-full animate-pulse" />
              {slide.tag}
            </span>

            {/* Headline */}
            <h1
              className="font-display font-black text-white leading-tight mb-5"
              style={{
                fontSize:   'clamp(2.8rem, 6vw, 5.5rem)',
                opacity:    animating ? 0 : 1,
                transform:  animating ? 'translateY(18px)' : 'translateY(0)',
                transition: 'opacity 0.55s ease, transform 0.55s ease',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              {slide.headline}{' '}
              <span style={{ color: '#fda4af', textShadow: '0 0 30px rgba(253,164,175,0.5)' }}>
                {slide.highlight}
              </span>
            </h1>

            {/* Subtitle */}
            <p
              className="font-body text-white/85 leading-relaxed mb-8"
              style={{
                fontSize:   'clamp(1rem, 1.5vw, 1.2rem)',
                maxWidth:   460,
                opacity:    animating ? 0 : 1,
                transition: 'opacity 0.55s ease 0.12s',
                textShadow: '0 1px 8px rgba(0,0,0,0.25)',
              }}
            >
              {slide.subtitle}
            </p>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <Link
                to={slide.ctaLink}
                className="group inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-body font-bold px-8 py-4 rounded-2xl transition-all hover:-translate-y-0.5"
                style={{ boxShadow: '0 6px 28px rgba(244,63,94,0.55)' }}
              >
                {slide.cta}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 border border-white/35 bg-white/15 backdrop-blur-sm text-white font-body font-semibold px-8 py-4 rounded-2xl hover:bg-white/25 transition-all"
              >
                <Gift size={18} className="text-rose-200" /> Browse All
              </Link>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              {[
                { value: '10,000+', label: 'Happy Couples'  },
                { value: '4.9★',   label: 'Average Rating' },
                { value: '500+',   label: 'Unique Gifts'   },
              ].map((s) => (
                <div key={s.label}>
                  <p className="font-display font-black text-white text-2xl" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                    {s.value}
                  </p>
                  <p className="text-white/60 text-sm font-body">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — glass live-orders card */}
          <div className="hidden lg:flex justify-end">
            <div className="w-72 rounded-3xl p-5 border border-white/20"
              style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}>

              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <p className="text-white/70 font-body text-xs font-bold uppercase tracking-widest">
                  Live Orders
                </p>
              </div>

              <div className="space-y-2.5">
                {[
                  { item: '💝 Surprise Love Box',   city: 'Mumbai',    price: '₹1,499', ago: 'Just now'  },
                  { item: '✨ Star Map Print',       city: 'Delhi',     price: '₹899',  ago: '4 min ago' },
                  { item: '🤝 Handmade Love Jar',   city: 'Pune',      price: '₹599',  ago: '9 min ago' },
                  { item: '💍 Engraved Keychain',   city: 'Bangalore', price: '₹349',  ago: '14 min ago'},
                ].map((o, i) => (
                  <div key={i} className="flex items-center justify-between rounded-2xl px-3 py-2.5"
                    style={{ background: 'rgba(255,255,255,0.10)' }}>
                    <div className="min-w-0">
                      <p className="text-white font-body font-semibold text-xs truncate">{o.item}</p>
                      <p className="text-white/50 font-body text-[10px]">{o.city} · {o.ago}</p>
                    </div>
                    <span className="text-rose-300 font-display font-bold text-sm shrink-0 ml-2">{o.price}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl px-3 py-2.5 border border-green-400/25"
                style={{ background: 'rgba(34,197,94,0.12)' }}>
                <p className="text-green-300 font-body text-xs font-semibold text-center">
                  🚚 Free delivery on all orders
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Slide dots ── */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => go(i)}
            className={`rounded-full transition-all duration-500 ${
              i === current
                ? 'w-8 h-2.5 bg-rose-400'
                : 'w-2.5 h-2.5 bg-white/35 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* ── Arrow buttons ── */}
      {['left', 'right'].map((dir) => (
        <button key={dir}
          onClick={() => go(dir === 'left'
            ? (current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length
            : (current + 1) % HERO_SLIDES.length
          )}
          className={`absolute ${dir}-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center transition-all hover:scale-105`}
          style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
        >
          {dir === 'left' ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>
      ))}

      {/* ── Keyframes ── */}
      <style>{`
        @keyframes heroZoom {
          from { transform: scale(1.07); }
          to   { transform: scale(1.00); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// RECIPIENT CATEGORY CARD
// ─────────────────────────────────────────────────────────────────
// function RecipientCard({ cat }) {
//   return (
//     <Link
//       to={`/products?recipient=${cat.id}`}
//       className="group relative rounded-3xl overflow-hidden block transition-all duration-400 hover:-translate-y-2"
//       style={{
//         aspectRatio: '3/4',
//         boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
//       }}
//     >
//       {/* Photo */}
//       <img
//         src={cat.image}
//         alt={cat.label}
//         className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
//         loading="lazy"
//       />

//       {/* Rose-pink gradient overlay */}
//       <div
//         className="absolute inset-0"
//         style={{
//           background: `linear-gradient(to top, rgba(136,19,55,0.75) 0%, rgba(190,24,93,0.40) 50%, rgba(244,63,94,0.10) 100%)`,
//           transition: 'opacity 0.3s',
//         }}
//       />

//       {/* Content */}
//       <div className="absolute inset-0 flex flex-col justify-end p-3.5">
//         <span className="text-2xl mb-1 block group-hover:scale-110 transition-transform duration-300">
//           {cat.emoji}
//         </span>
//         <h3 className="font-display font-bold text-white text-sm leading-tight drop-shadow">
//           {cat.label}
//         </h3>
//         <div className="flex items-center gap-1 mt-1.5 text-rose-200 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
//           <span className="text-xs font-body font-semibold">Shop Now →</span>
//         </div>
//       </div>
//     </Link>
//   );
// }

function RecipientCard({ cat }) {
  return (
    <Link
      to={`/products?recipient=${cat.id}`}
      className="group relative rounded-2xl overflow-hidden block transition-all duration-300 hover:-translate-y-2"
      style={{
        aspectRatio: "3/4",
        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
      }}
    >
      {/* Image */}
      <img
        src={cat.image}
        alt={cat.label}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />

      {/* Rose-pink gradient overlay */}
      <div
  className="absolute inset-0"
  style={{
    background: `linear-gradient(
      to top,
      rgba(244, 114, 182, 0.45) 0%,
      rgba(251, 207, 232, 0.25) 50%,
      rgba(255, 255, 255, 0.05) 100%
    )`,
    transition: "opacity 0.3s",
  }}
/>
      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end items-center text-center p-4">
  <h3 className="text-white font-semibold text-sm mb-2 drop-shadow-lg">
    {cat.label}
  </h3>

  <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-3 group-hover:translate-y-0">
    <button className="bg-white/90 backdrop-blur-md text-rose-500 text-xs px-4 py-1.5 rounded-md font-semibold shadow-md hover:bg-white transition-all duration-300">
      Shop Now →
    </button>
  </div>
</div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const { products, loading } = useProducts();

  const showFeat = (products.filter((p) => p.isFeatured).slice(0, 4).length > 0
    ? products.filter((p) => p.isFeatured)
    : products).slice(0, 4);

  const showNew = (products.filter((p) => p.isNew).slice(0, 4).length > 0
    ? products.filter((p) => p.isNew)
    : products.slice(4)).slice(0, 4);

  return (
    <div className="bg-white min-h-screen">

      {/* Hero */}
      <HeroSlider />

      {/* Trust bar */}
      <div className="bg-white border-b border-rose-100 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { Icon: Truck,     title: 'Free Delivery',   sub: 'On every order'      },
              { Icon: Shield,    title: 'Secure Payments', sub: 'Razorpay & UPI'      },
              { Icon: RefreshCw, title: 'Easy Returns',    sub: '7-day return policy' },
              { Icon: Gift,      title: 'Gift Wrapping',   sub: 'Complimentary'       },
            ].map(({ Icon, title, sub }) => (
              <div key={title} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-rose-500" />
                </div>
                <div>
                  <p className="font-body font-semibold text-gray-900 text-sm">{title}</p>
                  <p className="text-xs text-gray-400 font-body">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Who is it for */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-rose-400 font-body text-sm font-bold tracking-widest uppercase mb-2">Shop by Recipient</p>
          <h2 className="font-display font-bold text-4xl text-gray-900">Who Is It For? 🎁</h2>
          <p className="text-gray-400 font-body text-base mt-2 max-w-md mx-auto">
            Curated gifts for every special person in your life — girlfriend, wife, mother, and more.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {RECIPIENT_CATEGORIES.map((cat) => (
            <RecipientCard key={cat.id} cat={cat} />
          ))}
        </div>
      </section>

      {/* Bestsellers */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-rose-50/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-rose-400 font-body text-sm font-bold tracking-widest uppercase mb-2">Curated For You</p>
              <h2 className="font-display font-bold text-4xl text-gray-900">Bestselling Gifts</h2>
            </div>
            <Link to="/products" className="hidden md:flex items-center gap-1 text-rose-500 hover:text-rose-600 font-body font-medium">
              View All <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {loading
              ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
              : showFeat.length > 0
                ? showFeat.map((p) => <ProductCard key={p.id} product={p} />)
                : <div className="col-span-4 text-center py-16 text-gray-400 font-body">No products yet. Add from the merchant dashboard!</div>
            }
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-white text-center"
            style={{
              background: 'linear-gradient(135deg, #881337 0%, #be185d 50%, #f43f5e 100%)',
              boxShadow: '0 20px 60px rgba(136,19,55,0.35)',
            }}>
            {/* Decorative hearts */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
              {[...Array(14)].map((_, i) => (
                <span key={i} className="absolute text-4xl select-none"
                  style={{ left: `${(i % 7) * 15}%`, top: `${i < 7 ? 10 : 55}%`, transform: 'rotate(15deg)' }}>♡</span>
              ))}
            </div>
            <div className="relative z-10">
              <p className="font-body font-bold tracking-widest uppercase text-rose-200 text-sm mb-3">Special Offer</p>
              <h2 className="font-display font-bold text-4xl md:text-5xl mb-4">
                Surprise Boxes from <span className="text-yellow-300">₹999</span>
              </h2>
              <p className="text-rose-100 font-body text-lg mb-8 max-w-xl mx-auto">
                5–7 romantic handpicked gifts in every box. Each box unique. Each moment, unforgettable. 💕
              </p>
              <Link to="/products?category=surprise"
                className="inline-flex items-center gap-2 bg-white text-rose-700 hover:bg-rose-50 px-8 py-4 rounded-2xl font-bold text-lg transition-all hover:-translate-y-0.5 shadow-xl">
                Shop Surprise Boxes <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      {(loading || showNew.length > 0) && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-rose-400 font-body text-sm font-bold tracking-widest uppercase mb-2">Fresh & New</p>
              <h2 className="font-display font-bold text-4xl text-gray-900">New Arrivals</h2>
            </div>
            <Link to="/products?filter=new" className="hidden md:flex items-center gap-1 text-rose-500 hover:text-rose-600 font-body font-medium">
              See All <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {loading
              ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
              : showNew.map((p) => <ProductCard key={p.id} product={p} />)
            }
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-rose-400 font-body text-sm font-bold tracking-widest uppercase mb-2">Happy Couples</p>
            <h2 className="font-display font-bold text-4xl text-gray-900">Love Stories & Reviews 💕</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.id} className="bg-rose-50/70 rounded-3xl p-6 relative hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} className="text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-gray-600 font-body text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-body font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-400 font-body">{t.city} · {t.product}</p>
                  </div>
                </div>
                <div className="absolute top-5 right-5 text-rose-200 text-6xl font-serif leading-none select-none">"</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto text-center">
          <div className="text-4xl mb-4 inline-block">💌</div>
          <h2 className="font-display font-bold text-3xl text-gray-900 mb-3">Get Love in Your Inbox</h2>
          <p className="text-gray-400 font-body mb-8">
            Exclusive deals, new arrivals, and heartfelt gift ideas — every week.
          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input type="email" placeholder="your@email.com"
              className="flex-1 px-4 py-3.5 rounded-2xl border border-rose-200 focus:outline-none focus:border-rose-400 font-body text-gray-700" />
            <button className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-3.5 rounded-2xl font-body font-medium transition-all"
              style={{ boxShadow: '0 4px 15px rgba(244,63,94,0.3)' }}>
              Subscribe
            </button>
          </div>
          <p className="text-xs text-gray-400 font-body mt-3">No spam, only love. Unsubscribe anytime. 💕</p>
        </div>
      </section>
    </div>
  );
}
